import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { MessageSquare } from 'lucide-react';
import type { Colleague, ColleaguePage, ConversationSummary } from '@up2d8/contracts';
import {
  AppSubBar,
  Avatar,
  Button,
  Card,
  EmptyState,
  LoadingState,
  SearchInput,
  useToast,
} from '@up2d8/ui';
import { useSession } from '../../shared/session';
import { colleagueFilterQuery, type ColleagueFilter } from '../../shared/colleague-filter';
import { renderAppTabLink, useAppTabs } from './app-tabs';
import { DeskColleagues } from './desk/desk-colleagues';
import { DeskShell } from './desk/desk-shell';
import { MessagesIcon } from './messages-icon';
import { NotificationBell } from './notification-bell';

/**
 * De collega-lijst van de medewerker (Stap 34b, ADR-0051).
 *
 * Vier gegevens per regel en niets meer: naam, foto, afdeling, ploeg. Geen telefoonnummer en geen
 * e-mailadres — dat zou er een adresboek van maken dat bij vertrek mee naar buiten gaat. En geen
 * downloadknop: wie de lijst wil exporteren, gebruikt de beheerderskant met het bijhorende recht.
 *
 * Een subscherm en geen tab. De tabbalk heeft er vier, en de vijfde plek is voor Chat (Stap 34c) —
 * waar deze lijst dan de ingang voor wordt. Tot dan staat hij onder het profiel.
 */
export function AppColleaguesPage() {
  const { t } = useTranslation();
  const tabs = useAppTabs();
  const navigate = useNavigate();
  const { api, has } = useSession();

  // Uit het globale zoeken komt de collega die je aantikte: zijn naam staat in het zoekveld, en op
  // het bureauscherm staat zijn profielkaart open (testrapport 28 okt.).
  const fromSearch = useSearch({ strict: false }) as { persoon?: string; zoek?: string };
  const [search, setSearch] = useState(fromSearch.zoek ?? '');
  const [debounced, setDebounced] = useState(fromSearch.zoek ?? '');
  // Opnieuw zoeken terwijl je hier al staat: het scherm blijft staan, alleen de URL verandert.
  useEffect(() => {
    if (fromSearch.zoek === undefined) return;
    setSearch(fromSearch.zoek);
    setDebounced(fromSearch.zoek);
  }, [fromSearch.zoek, fromSearch.persoon]);
  // De filterknop van het bureauscherm. Hij gaat mee naar de server, zodat lijst, "Meer laden" en
  // de telling in de voet op dezelfde filter staan (testrapport 28 okt., #17).
  const [filter, setFilter] = useState<ColleagueFilter>(null);

  // Niet op elke toetsaanslag zoeken: op een telefoon met een trage verbinding levert dat een
  // lijst op die onder je vingers blijft verspringen.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const colleagues = useInfiniteQuery({
    queryKey: ['colleagues', debounced, filter],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<ColleaguePage> => {
      const { data, error } = await api.GET('/api/v1/colleagues', {
        params: {
          query: {
            ...(debounced ? { q: debounced } : {}),
            ...colleagueFilterQuery(filter),
            ...(pageParam ? { cursor: pageParam } : {}),
          },
        },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as ColleaguePage;
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const items = colleagues.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <DeskShell
      messages={<MessagesIcon />}
      bell={<NotificationBell />}
      tabs={tabs}
      activeKey="colleagues"
      renderLink={renderAppTabLink}
    >
      {/* --- Bureauscherm: de lijst met de profielkaart ernaast --- */}
      <div className="hidden desk:block">
        <DeskColleagues
          search={search}
          onSearch={setSearch}
          filter={filter}
          onFilter={setFilter}
          openPersonId={fromSearch.persoon ?? null}
          items={items}
          total={colleagues.data?.pages[0]?.total ?? items.length}
          loading={colleagues.isPending}
          hasMore={colleagues.hasNextPage}
          loadingMore={colleagues.isFetchingNextPage}
          onMore={() => void colleagues.fetchNextPage()}
        />
      </div>

      {/* --- Telefoon: de lijst zoals ze was --- */}
      <div className="flex flex-col gap-element desk:hidden">
        {/*
          De kopbalk met de terugknop hoort bij de **telefoon**, niet bij het bureauscherm: daar
          staat de navigatie links en is terug een stap in de geschiedenis van de browser.

          Ze staat hier en niet in `DeskShell`, omdat niet elk scherm er een heeft - de tijdlijn en
          de agenda zijn bestemmingen van de onderbalk en hebben nergens om naar terug te gaan.
          Ze geeft ook de `h1` van het scherm; zonder haar had deze pagina er geen meer.
      */}
        <AppSubBar
          title={t('colleagues.title')}
          onBack={() => void navigate({ to: '/app/profile' })}
        />
        <Card>
          <SearchInput
            id="colleague-search"
            label={t('colleagues.search')}
            placeholder={t('colleagues.searchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </Card>

        {colleagues.isPending ? (
          <LoadingState rows={5} label={t('common.loading')} />
        ) : items.length === 0 ? (
          <Card data-testid="colleagues-empty">
            <EmptyState
              title={t('colleagues.empty')}
              description={debounced ? t('colleagues.emptyHint') : undefined}
            />
          </Card>
        ) : (
          <Card>
            <ul className="flex flex-col" data-testid="colleague-list">
              {items.map((colleague) => (
                <li
                  key={colleague.personId}
                  data-testid="colleague"
                  className="flex min-h-touch items-center gap-element border-b border-border-light py-2 last:border-b-0"
                >
                  <ColleagueAvatar colleague={colleague} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-heading text-content font-medium text-text-primary">
                      {colleague.displayName}
                    </p>
                    {(colleague.department || colleague.shift) && (
                      <p className="truncate text-small text-text-muted">
                        {[colleague.department, colleague.shift].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </div>
                  {/* De enige weg naar een nieuw gesprek: je kiest een mens, geen gespreksvorm. */}
                  {has('messaging.conversation.use') && <StartConversation colleague={colleague} />}
                </li>
              ))}
            </ul>

            {colleagues.hasNextPage && (
              <Button
                variant="secondary"
                className="mt-element"
                loading={colleagues.isFetchingNextPage}
                onClick={() => void colleagues.fetchNextPage()}
              >
                {t('colleagues.more')}
              </Button>
            )}
          </Card>
        )}
      </div>
    </DeskShell>
  );
}

/**
 * De foto als die er is, anders de initialen.
 *
 * De download-URL is kortlevend en wordt per bestand opgehaald (ADR-0015), dus hij staat niet in de
 * lijstquery: dat zou vijfentwintig extra aanroepen in één antwoord persen.
 */
function ColleagueAvatar({ colleague }: { colleague: Colleague }) {
  const { api } = useSession();

  const photo = useQuery({
    queryKey: ['colleague-photo', colleague.photoFileId],
    enabled: colleague.photoFileId !== null,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await api.GET('/api/v1/media/{fileId}/download', {
        params: {
          path: { fileId: colleague.photoFileId as string },
          query: { variant: 'thumbnail' },
        },
      });
      if (error || !data) return null;
      return (data as { url: string }).url;
    },
  });

  if (photo.data) {
    return (
      <img
        src={photo.data}
        alt=""
        className="h-9 w-9 shrink-0 rounded-full object-cover"
        loading="lazy"
      />
    );
  }
  return <Avatar name={colleague.displayName} size="md" />;
}

/**
 * "Bericht sturen" bij een collega (Stap 34c).
 *
 * Opent het bestaande gesprek of begint een nieuw: de server beslist dat, en die aanroep is
 * idempotent. De client hoeft dus niet te weten of er al een gesprek was, en kan er ook niet per
 * ongeluk een tweede maken.
 */
function StartConversation({ colleague }: { colleague: Colleague }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const start = useMutation({
    mutationFn: async (): Promise<string> => {
      const { data, error } = await api.POST('/api/v1/conversations', {
        body: { personId: colleague.personId },
      });
      if (error || !data) throw error ?? new Error('Starten mislukt');
      return (data as ConversationSummary).id;
    },
    onSuccess: (conversationId) => {
      // Een nieuw gesprek staat nog niet in de gecachte lijst; zonder verversen vindt het
      // gesprekscherm het niet terug.
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      void navigate({ to: '/app/gesprekken/$conversationId', params: { conversationId } });
    },
    onError: () => {
      toast.show({ tone: 'danger', title: t('messaging.startFailed') });
    },
  });

  return (
    <button
      type="button"
      data-touch-target
      data-testid="start-conversation"
      aria-label={t('messaging.startWith', { name: colleague.displayName })}
      onClick={() => start.mutate()}
      disabled={start.isPending}
      className="inline-flex min-h-touch shrink-0 items-center justify-center rounded-pill p-2 text-text-secondary hover:bg-surface-sunk hover:text-brand-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary disabled:opacity-60"
    >
      <MessageSquare aria-hidden className="h-5 w-5" />
    </button>
  );
}
